<template>
    <base-modal hide-footer hide-header size="lg" @close="$emit('close')">
        <h3 class="editor-header">{{ !station || !station.id ? $t('power_stations.add') : $t('power_stations.edit')}}</h3>
        <div class="col-sm-12" @click.stop="null">
          <div v-if="station" class="col-sm-12 editor-form">
              <div class="col-12">
                  <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                      <div class="flex-grow-1">
                          <h4 class="form-header pointer hover-header" @click.stop="photosVisible = !photosVisible">
                              {{ $t('power_stations.photos') }} ({{ station && station.photos ? station.photos.length : 0 }})
                          </h4>
                      </div>
                      <div class="ms-2 px-2" @click.stop="photosVisible = !photosVisible">
                          <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: photosVisible }"/>
                      </div>
                  </div>
                  <div class="routa-collapse" :class="{ collapsed: photosVisible }">
                      <div class="stat-container nopads mb-3">
                          <div v-if="station.photos && station.photos.length > 0" class="col-12 nopads">
                              <Flicking ref="flicking"
                                        :options="options"
                                        :plugins="plugins"
                                        class="pointer nopads text-center"
                                        style="height: 15em;">
                                  <ImageView
                                      v-for="photo in station.photos" :key="photo.id"
                                      ref="image"
                                      class="flicking-panel mx-1"
                                      :contain="true"
                                      :url="getPhotoUrl(powerStationPhotoUrl, photo)"
                                      :show-delete-button="true"
                                      @click="openImage"
                                      @deleteImage="setPhotoForDeletion"
                                      :controlSize="`sm`"
                                  />
                                  <template #viewport>
                                      <div class="flicking-pagination"></div>
                                  </template>
                              </Flicking>
                          </div>
                          <div v-else style="padding-top: 6em; height: 15em; text-align: center">
                              {{ $t('power_stations.no_photos') }}
                          </div>
                      </div>

                      <!-- Photo editor-->
                      <div class="col-12">
                          <!-- Show selected photos -->
                          <div
                              v-for="(file, index) in photos"
                              :key="index"
                              class="d-flex align-items-center justify-content-between mb-3"
                          >
                              <span>{{ file?.name || file }}</span>
                              <button
                                  class="btn btn-outline-danger "
                                  type="button"
                                  @click="removePhoto(index)"
                              >
                                  {{ $t('common.remove') }}
                              </button>
                          </div>

                          <!-- Hidden file input -->
                          <input
                              ref="photoInput"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png"
                              class="visually-hidden"
                              @change="handlePhotoChange"
                          />

                          <!-- Custom "Add photo" button -->
                          <div class="col-12 d-flex justify-content-end">
                              <button
                                  class="btn btn-outline-primary "
                                  type="button"
                                  @click="triggerPhotoInput"
                              >
                                  {{ $t('power_stations.add_photo') }}
                              </button>
                          </div>
                      </div>
                  </div>
              </div>
            <hr>
            <h4 class="form-header">
              {{ $t('power_stations.basic_info') }}
            </h4>
            <div class="col-12 p-1">
              <span class="span-title">{{ $t('power_stations.location') }}</span>
              <span v-if="station && station.street">{{ station.street }} {{ station.city }}</span>
              <span
                v-else
                style="font-size: .85em"
              >{{ coordinate.lat.toFixed(6) + ' ' + coordinate.lng.toFixed(6) }}</span>
            </div>
            <div class="row">
              <div class="col-sm-4 p-1">
                <span class="span-title">{{ $t('street_lights.status') }}</span>
                  <div>
                      <select
                          id="lightStatus"
                          v-model="station.status"
                          class="form-select form-select-sm"
                      >
                          <option :value="null">
                              {{ $t('street_lights.select_status') }}
                          </option>
                          <option
                              v-for="option in stationStatusOptions"
                              :key="option.value"
                              :value="option.value"
                          >
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
              </div>
                <div class="col-sm-4 p-1">
                    <span class="span-title">{{ $t('power_stations.location_name') }}</span>
                    <input
                        v-model="station.location_name"
                        required
                        type="text"
                        class="form-control form-control-sm"
                    />
                </div>
                <div class="col-sm-4 p-1">
                    <span class="span-title">{{ $t('power_stations.location_code') }}</span>
                    <input
                        v-model="station.metering_point_number"
                        type="text"
                        class="form-control form-control-sm"
                    />
                </div>
            </div>
              <div class="row">
                  <div class="col-sm-4 p-1">
                      <span class="span-title">{{ $t('power_stations.type') }}</span>
                      <select
                          id="stationType"
                          v-model="station.station_type"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in stationTypeOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
                  <div class="col-sm-4 p-1">
                      <span class="span-title">{{ $t('power_stations.locking') }}</span>
                      <select
                          id="stationLocking"
                          v-model="station.locking"
                          class="form-select form-select-sm"
                      >
                          <option v-for="option in lockingOptions" :key="option.value" :value="option.value">
                              {{ option.text }}
                          </option>
                      </select>
                  </div>
                  <div class="col-sm-4 p-1">
                      <span class="span-title">{{ $t('power_stations.plate_value') }}</span>
                      <input
                          v-model="station.plate_value"
                          type="text"
                          class="form-control form-control-sm"
                      />
                  </div>
              </div>
            <hr>
            <h4 class="form-header mt-0 pt-0">
              {{ $t('power_stations.contract') }}
            </h4>
            <span class="tip-light">{{ $t('power_stations.contract_tip') }}</span>
            <open-contract-filter
              :to="toDate"
              :from="fromDate"
              :contract="preselectedContract"
              :show-company="false"
              :show-title="false"
              style="margin-left: -2px; margin-right: -3px;"
              @loading="loading=true"
              @loading-completed="loading=false"
              @contract-selected="setContract"
            />
            <hr>
          <div class="col-12">
              <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                  <div class="flex-grow-1">
                      <h4 class="form-header pointer hover-header" @click.stop="electricsVisible = !electricsVisible">
                          {{ $t('power_stations.electric_and_controls') }}
                      </h4>
                  </div>
                  <div class="ms-2 px-2" @click.stop="electricsVisible = !electricsVisible">
                      <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: electricsVisible }"/>
                  </div>
              </div>
              <div class="routa-collapse" :class="{ collapsed: electricsVisible }">
                  <div class="row">
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.main_fuse') }}</span>
                          <select
                              id="stationType"
                              v-model="station.main_fuse"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in mainFuseTypeOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.main_fuse_type') }}</span>
                          <select
                              id="stationLocking"
                              v-model="station.main_fuse_type"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in mainFuseOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                  </div>
                  <div class="row">
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.meter_number') }}</span>
                          <input
                              v-model="station.meter_number"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.night_mode') }}</span>
                          <input
                              type="checkbox"
                              v-model="station.shutdown_at_night"
                              class="form-check-input"
                          />
                      </div>
                  </div>
                  <div class="row">
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.energy_company') }}</span>
                          <input
                              v-model="station.energy_company"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.energy_company_number') }}</span>
                          <input
                              v-model="station.energy_company_number"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                  </div>
                  <div class="row">
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.metering') }}</span>
                          <select
                              id="stationType"
                              v-model="station.metering"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in meteredOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.power_source') }}</span>
                          <input
                              v-model="station.power_source_cable"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                  </div>
                  <div class="row">
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.control_method') }}</span>
                          <select
                              id="controlMethod"
                              v-model="station.control_method"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in controlTypeOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-6">
                          <span class="span-title">{{ $t('power_stations.control_code') }}</span>
                          <input
                              v-model="station.control_code"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                  </div>
                  <div class="row">
                      <div class="col-sm-4">
                          <span class="span-title">{{ $t('power_stations.max_outputs') }}</span>
                          <input
                              v-model="station.max_outputs"
                              type="number"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div class="col-sm-4">
                          <span class="span-title">{{ $t('power_stations.short_circuit_current') }}</span>
                          <input
                              v-model="station.short_circuit_current"
                              type="number"
                              step="any"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div class="col-sm-4">
                          <span class="span-title">{{ $t('power_stations.short_circuit_current_measure_date') }}</span>
                          <input
                              v-model="station.short_circuit_current_measure_date"
                              type="date"
                              class="form-control form-control-sm"
                          />
                      </div>
                  </div>
                  <span class="span-title upper">{{ $t('power_stations.group_fuses') }}</span>
                  <div v-for="(item, index) in groupFuses" :key="index" class="row align-items-end group-fuse pb-1">
                      <div class="col-sm-2">
                          <span class="span-title">{{ $t('power_stations.group_fuse_type') }}</span>
                          <select
                              id="stationType"
                              v-model="item.type"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in groupFuseTypeOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-2">
                          <span class="span-title">{{ $t('power_stations.circuit_break_curve') }}</span>
                          <select
                              id="stationType"
                              v-model="item.circuit_break_curve"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in breakCurveOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-2">
                          <span class="span-title">{{ $t('power_stations.current_value') }}</span>
                          <select
                              id="stationType"
                              v-model="item.current_value"
                              class="form-select form-select-sm"
                          >
                              <option v-for="option in currentValueOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                              </option>
                          </select>
                      </div>
                      <div class="col-sm-2">
                          <span class="span-title">{{ $t('power_stations.cable_protection_auto') }}</span>
                          <input
                              type="checkbox"
                              v-model="item.cable_protection_auto"
                              class="form-check-input"
                          />
                      </div>
                      <div class="col-sm-3">
                          <span class="span-title">{{ $t('power_stations.group_cable_type') }}</span>
                          <input
                              v-model="item.cable_type"
                              type="text"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div class="col-1 nopads text-center pb-1">
                          <button class="btn  btn-danger" @click.stop="removeGroupFuse(index)">
                              <i class="fa fa-trash"></i>
                          </button>
                      </div>
                  </div>
                  <div class="col-12 d-flex justify-content-end pt-2">
                      <button class="btn  btn-success mr-1" :disabled="groupFuses.length > 3" @click.stop="addGroupFuse">
                          <i class="fa fa-plus"></i>
                      </button>
                  </div>
              </div>
          </div>
            <hr/>
              <div class="col-12">
                  <div class="col-12 d-flex justify-content-between align-items-center pb-1">
                      <div class="flex-grow-1">
                          <h4 class="form-header pointer hover-header" @click.stop="inspectionsVisible = !inspectionsVisible">
                              {{ $t('power_stations.inspections') }}
                          </h4>
                      </div>
                      <div class="ms-2 px-2" @click.stop="inspectionsVisible = !inspectionsVisible">
                          <font-awesome-icon icon="arrow-down" class="pointer no-focus arrow-icon dont-print hover-header" :class="{ rotated: inspectionsVisible }"/>
                      </div>
                  </div>
                  <div class="routa-collapse" :class="{ collapsed: inspectionsVisible }">
                      <div class="col-12">
                              <span class="span-title">{{ $t('power_stations.renewal_need') }}</span>
                              <textarea
                                  id="renewal_need"
                                  v-model="station.renewal_need"
                                  class="form-control form-control-sm"
                                  rows="4"
                                  style="resize: vertical;"
                              ></textarea>
                      </div>
                      <div class="col-12 p-1">
                          <span class="span-title">{{ $t('power_stations.next_inspection_year') }}</span>
                          <input
                              v-model="station.next_inspection_year"
                              type="number"
                              min="2020"
                              max="2100"
                              class="form-control form-control-sm"
                          />
                      </div>
                      <div v-if="inspections && inspections.length > 0">
                          <!-- TODO - Inspection lists here -->
                      </div>
                          <span v-else-if="loading" class="text-left">{{ $t('common.loading') }}</span>
                      <span v-else class="text-left">{{ $t('power_stations.no_inspections') }}</span>
                  </div>
              </div>
            <hr>
            <h4 class="form-header mt-0 pt-0">
              {{ $t('power_stations.info') }}
            </h4>
              <textarea
                  id="info"
                  v-model="station.info"
                  class="form-control form-control-sm"
                  :placeholder="$t('power_stations.info')"
                  rows="4"
                  style="resize: vertical;"
              ></textarea>
              <div class="col-sm-12 button-container pt-4 pb-0 mb-0">
              <button
                class="btn btn-secondary form-button"
                @click.stop="$emit('close')"
              >
                {{ $t('common.cancel') }}
              </button>
              <button
                class="btn btn-success form-button"
                :disabled="loading||station===null"
                @click.stop="submit"
              >
                {{ $t('common.save') }}
              </button>
            </div>
          </div>
          <div
            v-if="loading"
            id="loader"
            class="spinner"
          />
        </div>
        <!-- Fullscreen photo -->
        <Transition name="fade">
          <fullscreen-image
            v-if="fullScreenPhotoData"
            :large-images="fullScreenPhotoData"
            :panel-index="panelIndex"
            @close="closePhoto"
          />
        </Transition>
  </base-modal>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import BaseModal from '../BaseModal'
import OpenContractFilter from "@/components/contract/OpenContractFilter";
import {timeUtils} from "@/components/mixins/TimeUtils";
import ImageView from "@/components/view/ImageView";
import FullscreenImage from "@/components/view/FullscreenImage";
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import {streetLightHelper} from "@/components/mixins/StreetLightMixin";
import {Pagination} from "@egjs/flicking-plugins";
import Flicking from "@egjs/vue3-flicking";
import "@egjs/flicking-plugins/dist/pagination.css";
import "@egjs/vue3-flicking/dist/flicking.css";
import {inject, toRaw} from "vue";

export default {
    name: 'PowerStationWizard',
    components: {FullscreenImage, OpenContractFilter, BaseModal, Flicking, ImageView},
    mixins: [restApi, timeUtils, attachmentHelper, streetLightHelper],
    props: {
        selectedContract: {
            type: Object,
            default: null
        },
        coordinate: {
            type: Object,
            default: null
        },
        powerStation: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            station: null,
            contract: null,
            fullScreenPhotoData: null,
            photosVisible: true,
            photoDeletionIds: [],
            electricsVisible: false,
            inspectionsVisible: false,
            inspectionsDownloaded: false,
            fromDate: new Date().toISOString(),
            toDate: new Date().toISOString(),
            inspections: [],
            photos: [],
            typeOptions: [
                {text: 'Type 1', value: 1},
                {text: 'Type 2', value: 2},
                {text: 'Type 4', value: 3}
            ],
            groupFuses: [],
            plugins: [new Pagination({ type: 'bullet' })],
            options: {
                preventDefaultOnDrag: true,
                circular: true,
                panelsPerView: 1
            },
            panelIndex: 0
        }
    },
    emits: ['close', 'closeAndUpdate'],
    computed: {
        preselectedContract() {
            return this.station.contract ? this.station.contract : null
        }
    },
    watch: {
        inspectionsVisible(value) {
            if (value && !this.inspectionsDownloaded) {
                // TODO - Fetch and show station's inspections
            }
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted() {
        if (!this.powerStation) {
            this.station = {
                id: null,
                street: null,
                city: null,
                location_name: null,
                metering_point_number: null,
                station_type: null,
                locking: null,
                plate_value: null,
                main_fuse: null,
                main_fuse_type: null,
                meter_number: null,
                energy_company: null,
                energy_company_number: null,
                metering: null,
                control_method: null,
                control_code: null,
                shutdown_at_night: false,
                renewal_need: '',
                power_source_cable: null,
                next_inspection_year: null,
                info: '',
                max_outputs: null,
                short_circuit_current: null,
                short_circuit_current_measure_date: null,
                position: this.coordinate,
                group_fuses: []
            }
        } else {
            this.station = toRaw(this.powerStation)
            this.groupFuses = this.station.group_fuses ? this.station.group_fuses : []
            this.contract = this.station.contract ? this.station.contract : null
            this.station.short_circuit_current_measure_date = this.timestampToDateInput(this.station.short_circuit_current_measure_date)
            this.fromDate = this.station.created_at ? this.station.created_at : this.fromDate
        }
    },
    methods: {
        triggerPhotoInput() {
            this.$refs.photoInput?.click();
        },
        handlePhotoChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.photos.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            this.photos.splice(index, 1);
        },
        addGroupFuse() {
            if (this.groupFuses.length < 4) {
                this.groupFuses.push({
                    type: null,
                    circuit_break_curve: null,
                    current_value: null,
                    cable_protection_auto: false,
                    cable_type: ""
                })
            }
        },
        removeGroupFuse(index) {
          if (this.groupFuses.length > index && index >= 0) {
              this.groupFuses.splice(index, 1)
          }
        },
        submit: function () {
            if (this.contract) {
                this.station.contract = this.contract
            }
            if (!this.station.position) {
                this.showAlert(this.$t('power_stations.error_position'))
                return;
            } else if (this.coordinate) {
                // Coordinates given as props for new station
                this.station.position = {
                    lat: this.coordinate.lat,
                    lon: this.coordinate.lng
                }
            }
            if (!this.station.location_name) {
                this.showAlert(this.$t('power_stations.error_location'))
                return;
            }
            this.station.group_fuses=this.groupFuses
            if (this.station.id && this.station.id > 0) {
                this.loading = true
                this.restUpdate(this.powerStationUrl, this.station, this.success, this.fail)
            } else {
                this.loading = true
                this.restAdd(this.powerStationUrl, this.station, this.success, this.fail)
            }
        },
        success: function (response) {
            let photos = this.getValidAttachments(this.photos)
            if (photos.length > 0) {
                this.uploadPhoto(photos, response.id)
            } else {
                this.uploadCompleted()
            }
        },
        uploadPhoto: function (photos, id) {
            if (photos.length > 0) {
                this.loading = true
                let formData = new FormData()
                photos.forEach(photoFile => {
                    if (photoFile !== undefined && photoFile !== null) {
                        formData.append('photos[]', photoFile)
                    }
                })
                this.restPostWithFile(this.powerStationPhotoUrl + '/' + id, {}, formData, this.uploadCompleted, this.fail)
            }
        },
        uploadCompleted: function () {
            this.loading = false
            if(this.photoDeletionIds && this.photoDeletionIds.length > 0) {
                this.deletePhotos()
            } else {
                this.showAlert(this.$t('common.alert_update_success'), 'success')
                this.$emit('closeAndUpdate')
            }
        },
        deletePhotos() {
            this.loading = true
            this.restDeleteItem(this.powerStationPhotoUrl + "/" + this.station.id, {photo_ids: this.photoDeletionIds}, this.handleDeleteResponse)
        },
        handleDeleteResponse: function () {
            this.loading = false
            this.photoDeletionIds = []
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('closeAndUpdate')
        },
        setPhotoForDeletion(photoId) {
            const id = Number(photoId);
            this.photoDeletionIds.push(id)
            // Handle the logic for deleting a photo here
            this.station.photos = this.station.photos.filter(photo => photo.id !== id);
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_add_failed'))
        },
        setContract: function (contract) {
            this.contract = contract
        },
        getPhotoUrl: function (baseUrl, photo) {
            return baseUrl + '/' + this.station.id + '/' + photo.id
        },
        openImage: function () {
            const flicking = this.$refs.flicking
            const imageData = this.$refs.image
            this.fullScreenPhotoData = []
            imageData.forEach(src => {
                this.fullScreenPhotoData.push(src)
            })
            this.panelIndex = flicking.index

        },
        closePhoto() {
            this.fullScreenPhotoData = undefined
        }
    }
}
</script>
<style>
@import "../../styles/modal.css";

.upper {
    text-transform: uppercase;
    margin-top: .75em;
    margin-bottom: 0;
    font-size: .9em;
}

.group-fuse {
    border: 1px #d9d9d9 solid;
    border-radius: .25em;
    margin-bottom: .3em;
}

</style>
